import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import { Card, CardContent, CardHeader } from '../../../ui/card';
import type { ApiFailureError } from '../../../lib/http/errors';
import { BreakdownGroups, BreakdownSelect } from '../shared/BreakdownSelect';
import type { Breakdown, Localities } from '../api/schemas';
import { tt } from './messages';
import { MovementCard } from './MovementCard';
import { figureOf, NeighbourhoodsCard, type RailMetric } from './NeighbourhoodsCard';
import { TerritoryMap, type MapShading } from './TerritoryMap';

/** The map shades on the figure the ranking shows, so both read one scale;
 *  without an answer it stays a boundary map. */
function shadingOf(localities: Localities | undefined, metric: RailMetric): MapShading | null {
  if (localities === undefined) return null;
  const values = localities.localities.map(
    (row) => [row.locality_id, figureOf(row, metric).value] as const,
  );
  const figures = values.flatMap(([, value]) => (value === null ? [] : [value]));
  return { values, max: figures.length === 0 ? null : Math.max(...figures) };
}

/** The electorate's headline figures, the map, and the right rail: the chosen
 *  breakdown dimension, the ranked localities and their movement. The
 *  electorate arrives rendered, since only it waits on an election; the map and
 *  the rail read the accounts and never do. */
export function TerritoryView(props: {
  electorate: ReactNode;
  tilesUrl: string;
  focus: string;
  campaignId: string;
  breakdown: UseQueryResult<Breakdown, ApiFailureError>;
  dimensionId: string | null;
  onDimensionChange: (dimensionId: string) => void;
  localities: UseQueryResult<Localities, ApiFailureError>;
  metric: RailMetric;
  onMetricChange: (metric: RailMetric) => void;
}): ReactNode {
  const { tilesUrl, localities, metric } = props;
  const answered = localities.status === 'success' ? localities.data : undefined;
  const shading = useMemo(() => shadingOf(answered, metric), [answered, metric]);
  return (
    <>
      <h1 className="sr-only">{tt('title')}</h1>
      {props.electorate}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex flex-col md:col-span-2">
          <TerritoryMap tilesUrl={tilesUrl} shading={shading} focus={props.focus} />
        </div>
        <div data-slot="rail" className="flex flex-col gap-4">
          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-title-sm">{tt('breakdown.title')}</h2>
              <BreakdownSelect
                campaignId={props.campaignId}
                value={props.dimensionId}
                onChange={props.onDimensionChange}
              />
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <BreakdownGroups query={props.breakdown} />
            </CardContent>
          </Card>
          <NeighbourhoodsCard
            query={localities}
            metric={metric}
            onMetricChange={props.onMetricChange}
          />
          <MovementCard query={localities} metric={metric} />
        </div>
      </div>
    </>
  );
}
